type MessageDictionary = Record<string, string>;

/**
 * Pending audit 2026-10-30 (WEB-1/WEB-2) — what `utils/axios.ts` puts on an error that got no answer
 * at all (network down, CORS, timeout). The same words Firebase's `auth/network-request-failed` has
 * had below since the start, so the two paths read alike.
 */
export const NETWORK_ERROR_MESSAGE = 'Error de conexión. Verificá tu internet e intentá nuevamente.';

// Exported (only) so error-translator.test.js can run a parametrized check over every entry
// instead of hand-writing one assertion per code — no change to runtime behavior.
export const ERROR_DICTIONARY: MessageDictionary = {
  // Authentication
  'User not found': 'Usuario no encontrado',
  'Wrong password': 'Contraseña incorrecta',
  'Email already in use': 'El correo electrónico ya está en uso',

  // Firebase Auth
  'auth/too-many-requests': 'Intentaste muchas veces. Probá nuevamente en unos minutos.',
  'auth/invalid-action-code': 'El enlace ya no es válido. Solicitá uno nuevo.',
  'auth/expired-action-code': 'El enlace ya no es válido. Solicitá uno nuevo.',
  'auth/popup-closed-by-user': 'No se completó el inicio de sesión. Podés intentarlo nuevamente.',
  'auth/account-exists-with-different-credential': 'Ya existe una cuenta con ese correo. Probá con otro método de inicio de sesión.',
  'auth/invalid-email': 'Ingresá un correo válido.',
  'auth/network-request-failed': NETWORK_ERROR_MESSAGE,

  // Shipment Validations
  'Pickup date cannot be in the past': 'La fecha de recogida no puede ser en el pasado',
  'Pickup start date is required': 'La fecha de recogida es requerida',
  'End date must be after start date': 'La fecha de fin debe ser posterior a la fecha de inicio',
  'Starting available date cannot be in the past': 'La fecha de inicio no puede ser en el pasado',
  'shipmentType is required (packages, vehicles, or moving)': 'El tipo de envío es requerido',
  'Origin and destination addresses are required': 'La dirección de origen y destino son requeridas',
  'User ID is required to create a shipment': 'Se requiere ID de usuario para crear el envío',
  'This shipment has expired and no longer accepts quotes': 'Este envío ha vencido y ya no acepta cotizaciones',
  'Height must be positive': 'El alto debe ser positivo',
  'Width must be positive': 'El ancho debe ser positivo',
  'Length must be positive': 'El largo debe ser positivo',
  'Depth must be positive': 'El largo debe ser positivo',

  // Saved Addresses
  'You already have an address with this name': 'Ya tenés una dirección con ese nombre.',
  'Address with this name already exists': 'Ya existe una dirección con este nombre.',

  // Generic
  'Bad Request': 'Solicitud incorrecta',
  'Internal Server Error': 'Error interno del servidor',
  Unauthorized: 'No autorizado',

  // Cancel Shipment
  'Cannot cancel a shipment with an accepted quote': 'No es posible cancelar esta solicitud porque ya tiene una cotización aceptada.',
  'Shipment cannot be cancelled in its current status': 'No es posible cancelar esta solicitud en su estado actual.',
  'Unauthorized to cancel this shipment': 'No tenés permiso para cancelar esta solicitud.',

  // Reviews
  'Delivery must be confirmed before rating the carrier': 'Debés confirmar la entrega antes de calificar al transportista.',
  'A review has already been submitted for this shipment': 'Ya enviaste una calificación para este envío.',
  'The review period for this shipment has expired': 'El plazo para calificar este envío ha vencido.',
  'Rating is required and must be between 1 and 5': 'La calificación es obligatoria y debe ser entre 1 y 5.',
  'Unauthorized: Only the shipment owner can rate the carrier': 'No tenés permiso para calificar este envío.',
  'Unauthorized: Only the assigned driver can rate the client': 'Solo el transportista asignado puede calificar al cliente.',
  'Shipment must be completed before rating the client': 'El envío debe estar completado antes de calificar al cliente.',

  // Q&A
  'Phone numbers are not allowed in questions. Please continue the conversation within the platform.':
    'No es posible compartir números de teléfono en preguntas. Por favor, continuá la conversación dentro de la plataforma.',
  'Phone numbers are not allowed in answers. Please continue the conversation within the platform.':
    'No es posible compartir números de teléfono en respuestas. Por favor, continuá la conversación dentro de la plataforma.',
  'This shipment no longer accepts questions': 'Esta solicitud ya no admite preguntas.',
  'This shipment no longer accepts answers': 'Esta solicitud ya no admite respuestas.',
  'This question has already been answered': 'Esta pregunta ya fue respondida.',
  'Question text is required': 'El texto de la pregunta es obligatorio.',
  'Answer text is required': 'El texto de la respuesta es obligatorio.',
  'Unauthorized: Only the shipment owner can answer questions': 'Solo el dueño de la solicitud puede responder preguntas.',
  'This feature is currently disabled': 'Esta funcionalidad no está disponible en este momento.',

  // Verification PIN
  'No active verification code found for this shipment': 'No se encontró un código de verificación activo para este envío.',
  'Forbidden: you are not the owner of this shipment': 'No tenés permiso para realizar esta acción.',
  'Maximum regeneration limit reached': 'Se alcanzó el límite de regeneraciones. Contactá a soporte.',

  // Delivery Receiver
  'Receiver full name is required': 'El nombre completo de quien recibe es obligatorio.',
  'Receiver full name cannot be empty': 'El nombre completo de quien recibe no puede estar vacío.',
  'Receiver document ID is required': 'El documento de identidad de quien recibe es obligatorio.',
  'Receiver document ID cannot be empty': 'El documento de identidad no puede estar vacío.',
  'Receiver signature is required': 'La firma de conformidad es obligatoria.',
  'Receiver signature cannot be empty': 'La firma de conformidad no puede estar vacía.',

  // NEX-1382 (2026-09): quote.controller.ts and vehicle.controller.ts used to build most of
  // their error responses by hand and drop the backend's `code` field entirely — only 5
  // whitelisted quote codes and vehicle's `changeActive` action forwarded it. That backend gap
  // is now closed (both controllers forward every error to next(err), same pattern as the
  // other 23 modules), so every entry below now ALSO has a matching CODE_DICTIONARY entry —
  // see that block further down. This block stays as the fallback for the rare case a request
  // somehow arrives with a message but no matching code (an unrelated backend wording change
  // silently stops the match here too, same fragility as every other entry in this
  // dictionary), and is otherwise redundant now rather than the primary mechanism it used to
  // be. Left in place rather than deleted: pure upside, zero risk, since `code` is always
  // checked first.

  // quote/
  'Quote not found': 'Cotización no encontrada.',
  'Shipment not found for this quote': 'No se encontró el envío para esta cotización.',
  'Shipment not found': 'Envío no encontrado.',
  'Driver profile not found': 'No se encontró tu perfil de transportista.',
  'You can only cancel your own quotes': 'Solo podés cancelar tus propias cotizaciones.',
  'Quote is not in a cancellable state': 'La cotización no está en un estado que permita cancelarla.',
  'User ID is required to create quotes': 'Se requiere el ID de usuario para crear cotizaciones.',
  'At least one quote is required': 'Se requiere al menos una cotización.',
  'Your account is disabled. Re-enable your account to start quoting.': 'Tu cuenta está deshabilitada. Reactivala para poder cotizar.',
  'Driver has no active vehicles': 'No tenés vehículos activos.',
  'Vehicle not found for this driver': 'No se encontró el vehículo para este transportista.',
  'Vehicle is not active': 'El vehículo no está activo.',
  'You cannot quote on your own shipment': 'No podés cotizar en tu propio envío.',
  'Invalid duration specified': 'La duración indicada no es válida.',
  'You can only view preview for your own quotes': 'Solo podés ver la vista previa de tus propias cotizaciones.',
  'Unauthorized to view this quote': 'No tenés permiso para ver esta cotización.',
  'Shipment is not available for quoting': 'El envío no está disponible para cotizar.',
  'Shipment time slot is invalid': 'El horario del envío no es válido.',
  'The time slot for this shipment has already ended': 'El horario para este envío ya finalizó.',
  "The proposed date must be within the shipment's available range":
    'La fecha propuesta tiene que estar dentro del rango disponible del envío.',
  'Driver profile not found for this user': 'No se encontró el perfil de transportista para este usuario.',
  'Unauthorized to view quotes for this shipment': 'No tenés permiso para ver las cotizaciones de este envío.',
  'Unauthorized to edit this quote': 'No tenés permiso para editar esta cotización.',
  'This quote has expired and cannot be edited': 'Esta cotización venció y ya no se puede editar.',
  'This shipment has expired and no longer accepts quote edits': 'Este envío venció y ya no acepta ediciones de cotizaciones.',
  'Cannot cancel a quote for a shipment that is already in transit or completed':
    'No se puede cancelar una cotización de un envío que ya está en tránsito o completado.',
  'Invalid reasonCode. Allowed: VEHICLE_BREAKDOWN, PERSONAL_EMERGENCY, SCHEDULE_CONFLICT, PRICING_ERROR, OTHER':
    'El motivo indicado no es válido.',
  'Cannot quote: you have an accepted quote on the same date that conflicts':
    'No podés cotizar: ya tenés una cotización aceptada en la misma fecha que genera un conflicto.',
  'Cannot quote: you have an accepted quote on the same date that conflicts with this time slot':
    'No podés cotizar: ya tenés una cotización aceptada en la misma fecha que genera un conflicto de horario.',
  'Maximum 3 quotes per batch allowed': 'Se permiten un máximo de 3 cotizaciones por lote.',

  // vehicle/
  'Driver profile not found for authenticated user': 'No se encontró tu perfil de transportista.',
  'This license plate is already registered to a verified driver.':
    'Esta patente ya está registrada y verificada a nombre de otro transportista.',
  "When type is 'Otro', otherType field is required": 'Si elegís "Otro", tenés que especificar el tipo de vehículo.',
  'Unauthorized to view this vehicle': 'No tenés permiso para ver este vehículo.',
  'Vehicle not found': 'Vehículo no encontrado.',
  'Vehicle plate is required': 'La patente es obligatoria.',
  'Brand not found': 'Marca no encontrada.',

  // NEX-1382 (segunda tanda, post-migración de quote/vehicle): estos dos casos NO son
  // quote/vehicle — son AppError sin tercer argumento (`code`), por diseño, en otros dos
  // módulos. A diferencia de quote/vehicle (que descartaban el code en el controller aunque
  // el handler SÍ lo asignaba), acá el propio handler nunca lo asigna: no hay code que
  // capturar del lado del backend, así que el mensaje-como-clave sigue siendo la única vía.

  // shipment/ — create-shipment.handler.ts y publish-draft.handler.ts, mismo mensaje exacto
  'Shipment creation is not available yet. We will notify you by email when it opens for your account.':
    'La creación de envíos todavía no está disponible para tu cuenta. Te avisaremos por correo cuando se habilite.',

  // customer/ — update-my-profile.handler.ts, 5 validaciones sin code
  'First name must be between 1 and 100 characters': 'El nombre debe tener entre 1 y 100 caracteres.',
  'Last name must be between 1 and 100 characters': 'El apellido debe tener entre 1 y 100 caracteres.',
  'Invalid phone number format': 'El formato del número de teléfono no es válido.',
  'Department must not exceed 100 characters': 'El departamento no puede superar los 100 caracteres.',
  'City must not exceed 100 characters': 'La ciudad no puede superar los 100 caracteres.'
};

// NEX-1371: the backend now sends a stable `code` alongside `message` for every error
// (see docs/NEX-1305-error-catalog.md in nexvia-api-web). Keying translations by `code`
// survives future wording changes — unlike ERROR_DICTIONARY above, which breaks silently
// the moment the backend rewords a message. Only codes whose message is unambiguous (one
// exact English text, one Spanish translation) are listed here — several backend codes are
// intentionally reused across multiple distinct messages (e.g. a shared "not found"/generic
// validation code, or one code covering both the client-side and driver-side of the same
// rule) and are left out on purpose, since a single translation would be wrong for one of
// the two messages that code covers. Those keep working today via the message-keyed lookup
// below — this dictionary is additive, not a replacement.
export const CODE_DICTIONARY: MessageDictionary = {
  SHIPMENT_PICKUP_DATE_PAST: 'La fecha de recogida no puede ser en el pasado',
  SHIPMENT_REPUBLISH_PICKUP_DATE_PAST: 'La fecha de recogida no puede ser en el pasado',
  SHIPMENT_PICKUP_START_REQUIRED: 'La fecha de recogida es requerida',
  SHIPMENT_END_DATE_BEFORE_START: 'La fecha de fin debe ser posterior a la fecha de inicio',
  SHIPMENT_TYPE_REQUIRED: 'El tipo de envío es requerido',
  SHIPMENT_ADDRESSES_REQUIRED: 'La dirección de origen y destino son requeridas',
  QUOTE_SHIPMENT_EXPIRED_NO_QUOTES: 'Este envío ha vencido y ya no acepta cotizaciones',
  SHIPMENT_CANCEL_INVALID_STATUS: 'No es posible cancelar esta solicitud en su estado actual.',
  SHIPMENT_CANCEL_UNAUTHORIZED: 'No tenés permiso para cancelar esta solicitud.',
  SHIPMENT_RECEIVER_NAME_REQUIRED: 'El nombre completo de quien recibe es obligatorio.',
  SHIPMENT_RECEIVER_DOCUMENT_ID_REQUIRED: 'El documento de identidad de quien recibe es obligatorio.',
  SHIPMENT_RECEIVER_SIGNATURE_REQUIRED: 'La firma de conformidad es obligatoria.',
  REVIEW_RATING_INVALID: 'La calificación es obligatoria y debe ser entre 1 y 5.',
  REVIEW_DELIVERY_NOT_CONFIRMED: 'Debés confirmar la entrega antes de calificar al transportista.',
  REVIEW_SHIPMENT_NOT_COMPLETED: 'El envío debe estar completado antes de calificar al cliente.',
  REVIEW_SHIPMENT_EXPIRED: 'El plazo para calificar este envío ha vencido.',
  REVIEW_ALREADY_SUBMITTED: 'Ya enviaste una calificación para este envío.',
  QUESTION_FEATURE_DISABLED: 'Esta funcionalidad no está disponible en este momento.',
  QUESTION_ALREADY_ANSWERED: 'Esta pregunta ya fue respondida.',
  QUESTION_UNAUTHORIZED_ANSWER: 'Solo el dueño de la solicitud puede responder preguntas.',
  SAVED_ADDRESS_DUPLICATE_LABEL: 'Ya tenés una dirección con ese nombre.',
  PAYMENT_PIN_REGENERATION_LIMIT_REACHED: 'Se alcanzó el límite de regeneraciones. Contactá a soporte.',

  // NEX-1382 audit (2026-09): every code below is already sent by the backend today
  // (`err.code` reaches error.middleware.ts, or the endpoint's own catch forwards it) — this
  // was verified file:line against the current nexvia-api-web source, module by module.

  // These 3 generic, backend-wide codes each resolve to a single fixed message today, verified
  // against every real call site (app-error.ts's own static helpers, so guaranteed
  // one-message-per-code by construction, unlike the multi-message generic codes excluded
  // above) — added here on that basis, not blindly:
  INTERNAL_SERVER_ERROR: 'Ocurrió un error interno. Intentá nuevamente en unos minutos.',
  SERVER_CONFIG_ERROR: 'Hay un problema de configuración del servidor. Contactá a soporte.',
  CAPTCHA_VALIDATION_FAILED: 'No se pudo validar el captcha. Intentá nuevamente.',

  // Payment / Checkout / Delivery PIN (payment/)
  PAYMENT_QUOTE_AMOUNT_INVALID: 'El monto de la cotización es obligatorio y debe ser un número positivo.',
  PAYMENT_CONFIG_NOT_FOUND_FOR_COUNTRY: 'No hay una configuración de pago activa para tu país.',
  PAYMENT_AUTH_REQUIRED: 'Necesitás iniciar sesión para continuar.',
  PAYMENT_CHECKOUT_QUOTE_ID_REQUIRED: 'Falta indicar la cotización a pagar.',
  PAYMENT_QUOTE_NOT_PENDING: 'Solo se pueden seleccionar para pago las cotizaciones pendientes.',
  PRICE_CHANGED: 'El precio de la cotización cambió.',
  PAYMENT_QUOTE_EXPIRED: 'Esta cotización venció.',
  PAYMENT_QUOTE_CARRIER_UNAVAILABLE: 'Este transportista no está disponible en este momento. Elegí otra cotización.',
  // Owner decision 35 (API-3): a new checkout is refused while an approved payment of the request
  // waits for the backoffice. Nothing was charged by the refused attempt.
  PAYMENT_UNDER_REVIEW: 'Tu pago está en revisión. No hace falta que vuelvas a pagar.',
  // Client phone verification (nexvia-api-web spec 2026-10-12, §4.4): POST /payment/checkout refuses
  // a client whose phone is not verified.
  CLIENT_PHONE_NOT_VERIFIED: 'Verificá tu teléfono para aceptar la cotización.',
  PAYMENT_UNAUTHORIZED_SHIPMENT: 'No tenés permiso para pagar este envío.',
  PAYMENT_SHIPMENT_STATUS_INVALID: 'El envío no está disponible para pago en su estado actual.',
  PAYMENT_SHIPMENT_EXPIRED: 'Este envío venció.',
  // NEX-1151 (audit W1) — the storage checkout and subscription answer it too: no "envío" in it.
  PAYMENT_ALREADY_IN_PROGRESS: 'Ya hay un pago en proceso. Esperá a que termine o intentá de nuevo en unos minutos.',
  PAYMENT_CONFIG_NOT_AVAILABLE: 'La configuración de pago no está disponible en este momento.',
  PAYMENT_UNAUTHORIZED_VIEW: 'No tenés permiso para ver este pago.',
  PAYMENT_PIN_UNAUTHORIZED: 'No tenés autorización para realizar esta acción.',
  PAYMENT_PIN_FORBIDDEN_NOT_OWNER: 'No sos el dueño de este envío.',
  PAYMENT_PIN_SHIPMENT_TERMINAL: 'No se puede regenerar el PIN: el envío ya fue completado o cancelado.',
  PAYMENT_PIN_NO_CODE_FOUND: 'No se encontró un código de verificación para este envío.',
  PAYMENT_PIN_NOT_LOCKED: 'El PIN solo se puede regenerar cuando está bloqueado por demasiados intentos fallidos.',
  SHIPMENT_VERIFICATION_CODE_REQUIRED: 'El código de verificación es obligatorio.',
  SHIPMENT_VERIFICATION_CODE_FORMAT_INVALID: 'El código de verificación debe tener exactamente 6 dígitos.',
  SHIPMENT_PIN_NOT_ASSIGNED_DRIVER: 'No sos el transportista asignado a este envío.',

  // Storage ("Guardado") — payment/checkout, payment/subscribe (NEX-1151). Verified against
  // nexvia-api-web's real handlers this session: create-storage-checkout.handler.ts and
  // create-storage-subscription.handler.ts both throw the first two codes verbatim;
  // get-storage-request-quotes.handler.ts throws the third. PAYMENT_ALREADY_IN_PROGRESS,
  // PAYMENT_QUOTE_NOT_PENDING and PAYMENT_QUOTE_EXPIRED are also real codes those same two
  // handlers use, but already have a generic-enough entry above — not duplicated here.
  PAYMENT_UNAUTHORIZED_REQUEST: 'No tenés permiso para pagar esta solicitud de depósito.',
  PAYMENT_REQUEST_STATUS_INVALID: 'Esta solicitud ya no está disponible para pago.',
  // NEX-1164 — 409 from the same two handlers when the quoting depósito is no longer `verified`
  // (suspended, rejected or back in review after replacing a document).
  PAYMENT_STORAGE_DEPOT_NOT_VERIFIED: 'Este depósito ya no está habilitado para operar. Elegí otra cotización.',
  // NEX-1169 (plan Fase 7.2) — 422 from create-storage-checkout.handler.ts: the pago único covers the
  // whole period, so an open-ended request (or one without a length) can only be paid monthly.
  PAYMENT_STORAGE_PERIOD_OPEN_ENDED: 'Tu depósito no tiene fecha de fin, así que no se puede pagar de una sola vez. Elegí el pago mensual.',
  PAYMENT_STORAGE_PERIOD_INVALID: 'Esta solicitud no tiene una duración para pagar de una sola vez. Elegí el pago mensual.',
  // NEX-1169 (plan 1.1) — 409 from create-storage-subscription.handler.ts: Mercado Pago does not create the
  // monthly subscription without the payer's email, and the client account has none.
  PAYMENT_STORAGE_PAYER_EMAIL_REQUIRED: 'Tu cuenta no tiene un correo para asociar el cobro. Completalo en tu perfil.',
  PRICING_SNAPSHOT_MISSING: 'Esta cotización todavía no tiene un precio calculado. Volvé a intentar en un momento.',
  STORAGE_UNAUTHORIZED_VIEW_REQUEST: 'No tenés permiso para ver las cotizaciones de esta solicitud de depósito.',
  // NEX-1170 — the client's guardado lifecycle endpoints (client-guardado.handler.ts): baja, renovación,
  // retiro and "Retiré por mi cuenta".
  STORAGE_STATUS_TRANSITION_INVALID: 'Tu depósito cambió de estado y esta acción ya no está disponible. Actualizá la página.',
  STORAGE_REQUEST_NOT_CONTRACTED: 'Esta solicitud todavía no tiene un depósito contratado.',
  STORAGE_GUARDADO_FORBIDDEN: 'No tenés permiso para gestionar este depósito.',
  // NEX-1169 (plan 2026-10-20 task 1.8) — POST /storage/requests/:id/renew: the 501
  // STORAGE_RENEWAL_PENDING_PAYMENTS is gone; only an `active` one-time guardado renews.
  STORAGE_RENEWAL_NOT_ALLOWED: 'Solo podés renovar un depósito activo.',
  STORAGE_RENEWAL_IS_THE_SUBSCRIPTION: 'Tu depósito se paga mes a mes: se renueva solo con la suscripción.',
  // NEX-1169 (plan 2026-10-20 task 1.5) — 409 from POST /storage/requests/:id/subscription/cancel: a one-time
  // guardado, or a monthly charge already cancelled.
  STORAGE_SUBSCRIPTION_NOT_LIVE: 'Este depósito no tiene un cobro mensual activo para cancelar.',
  STORAGE_WITHDRAWAL_ORIGIN_UNKNOWN: 'No tenemos la dirección del depósito que elegiste. Escribinos a soporte para coordinar el retiro.',

  // Storage — publicación de la solicitud (NEX-1166): create-storage-request.handler.ts y
  // request-storage-request-photo-upload-url.handler.ts de nexvia-api-web, misma rama.
  STORAGE_REQUEST_PHOTO_URL_NOT_ALLOWED: 'Las fotos tienen que subirse desde el formulario. Quitalas y volvé a subirlas.',
  STORAGE_REQUEST_PHOTO_INVALID: 'Una de las fotos no es válida. Quitala y volvé a subirla.',
  STORAGE_REQUEST_PHOTOS_TOO_MANY: 'Podés subir hasta 10 fotos.',
  STORAGE_REQUEST_PHOTO_NOT_UPLOADED: 'Una de las fotos no terminó de subirse. Quitala y volvé a subirla.',
  STORAGE_REQUEST_PHOTO_CONTENT_INVALID: 'No pudimos procesar una de las fotos. Probá con otra imagen en JPG, PNG o WEBP.',
  STORAGE_REQUEST_PHOTO_TOO_LARGE: 'La foto supera los 10 MB. Subí una versión más chica.',
  STORAGE_REQUEST_SERVICE_CODES_UNKNOWN:
    'Alguno de los opcionales elegidos ya no está disponible. Revisá los opcionales y volvé a intentar.',
  STORAGE_REQUEST_TRANSPORT_INCONSISTENT: 'Para cotizar el traslado con Nexvia, indicá que necesitás traslado.',
  STORAGE_REQUEST_PERIOD_REQUIRED: 'Indicá por cuántos meses necesitás el espacio o marcá que es sin fecha de fin.',
  STORAGE_REQUEST_PERIOD_INVALID: 'La cantidad de meses tiene que ser mayor a 0.',
  // Pending audit 2026-10-30 (CTR-7) — POST /storage/requests/photos/upload without a file
  // (storage.controller.ts) and multer refusing the multipart (shared/single-file-upload.ts:
  // a second file, a wrong field name).
  UPLOAD_FILE_REQUIRED: 'No se proporcionó ningún archivo.',
  UPLOAD_FILE_INVALID: 'No pudimos leer el archivo. Probá con otro.',
  // NEX-1166 — 422 from POST /storage/requests/photos/upload(-url) for an image/heic or
  // image/heif photo (nexvia-api-web domain/photo-format.ts): the depósitos see the photos in a browser.
  STORAGE_PHOTO_HEIC_NOT_SUPPORTED: 'Las fotos HEIC no se pueden ver en el navegador. Subí la foto en JPG, PNG o WEBP.',
  // NEX-1168 (plan 2026-10-20, Tarea 2.6) — 422 from POST /storage/requests: a calculator key the API does not know,
  // or a count outside 0–999 (`data.unknownKeys` / `data.invalidKeys`; nothing is saved).
  STORAGE_REQUEST_CALCULATOR_ITEMS_INVALID: 'Revisá los objetos que cargaste en la calculadora: alguno no es válido.',

  // NEX-1168 (plan 2026-10-20, Tarea 2.6) — POST /shipments and POST /shipments/drafts/:id/publish linked to a depósito
  // request (`storageRequestId` + `storageStop`, nexvia-api-web contract §1; the texts the contract suggests). The
  // shipment is not created when the link is refused.
  SHIPMENT_STORAGE_STOP_REQUIRES_MOVING: 'Solo una mudanza puede llevar parte de la carga al depósito.',
  SHIPMENT_STORAGE_REQUEST_NOT_LINKABLE: 'Esta solicitud de depósito ya no está vigente.',
  SHIPMENT_STORAGE_DESTINATION_NOT_ASSIGNED: 'Todavía no elegiste ni pagaste un depósito para esta solicitud.',
  SHIPMENT_STORAGE_DESTINATION_MISMATCH: 'El destino tiene que ser la dirección del depósito.',
  // NEX-1645 (plan 2026-10-20, Tarea 3.1) — 503 from requireStorageModule (nexvia-api-web) when the module is
  // `off`, or `registration_only` on what needs `full` (publishing, quoting, paying, renewing). The web hides
  // or disables those (GET /app/features), so this only shows when the backoffice switched it meanwhile.
  STORAGE_MODULE_DISABLED: 'Los depósitos todavía no están disponibles.',
  STORAGE_REQUESTS_DISABLED: 'Por ahora no se pueden pedir, contratar ni renovar depósitos. Próximamente.',

  // Shipment: borradores, edición, imágenes, entrega, republicación (shipment/)
  SHIPMENT_DRAFT_USER_ID_REQUIRED: 'Se requiere el ID de usuario para crear el borrador.',
  SHIPMENT_DRAFT_DATA_INVALID: 'Los datos del borrador no son válidos.',
  SHIPMENT_IMAGE_NOT_LINKED: 'La imagen no está asociada a ningún envío.',
  SHIPMENT_IMAGE_DELETE_FORBIDDEN: 'No tenés permiso para eliminar esta imagen.',
  SHIPMENT_IMAGE_URL_FORBIDDEN: 'No tenés permiso para acceder a esta imagen.',
  SHIPMENT_NOT_QUOTABLE_FOR_FEATURE: 'Solo se pueden destacar envíos disponibles para cotizar.',
  SHIPMENT_OWN_SHIPMENT_FEATURE_FORBIDDEN: 'No podés destacar tu propio envío.',
  SHIPMENT_NOT_OWNER_CONFIRM_RECEIPT: 'Solo quien creó el envío puede confirmar la recepción.',
  SHIPMENT_CONFIRM_RECEIPT_INVALID_STATUS: 'No se puede confirmar la recepción en el estado actual del envío.',
  SHIPMENT_IMAGE_CONFIRM_FORBIDDEN: 'No tenés permiso para confirmar esta imagen.',
  SHIPMENT_IMAGE_STATUS_INVALID: 'La imagen no está en un estado válido para esta acción.',
  SHIPMENT_IMAGE_VALIDATION_FAILED: 'No se pudo validar la imagen. Probá con otra foto.',
  SHIPMENT_DRIVER_PROFILE_NOT_FOUND: 'No se encontró tu perfil de transportista.',
  SHIPMENT_NOT_ASSIGNED_DRIVER: 'No sos el transportista asignado a este envío.',
  SHIPMENT_START_TRIP_INVALID_STATUS: 'No se puede iniciar el viaje hacia el retiro en el estado actual del envío.',
  SHIPMENT_IMAGE_TOO_LARGE: 'El archivo es demasiado grande. Máximo 10 MB.',
  SHIPMENT_CANCEL_PREVIEW_FORBIDDEN: 'No tenés permiso para ver la vista previa de cancelación.',
  SHIPMENT_OR_DRAFT_NOT_FOUND: 'No se encontró el envío o el borrador.',
  SHIPMENT_UPLOAD_PERMISSION_DENIED: 'No tenés permiso para subir fotos para este envío.',
  SHIPMENT_UPDATE_NOT_OWNER: 'No tenés permiso para actualizar este envío.',
  SHIPMENT_NOT_PENDING_FOR_UPDATE: 'Solo se pueden actualizar envíos en estado pendiente.',
  SHIPMENT_UPDATE_EXPIRED: 'No se puede actualizar un envío vencido.',
  SHIPMENT_UPDATE_PICKUP_START_PAST: 'La fecha de recogida no puede ser en el pasado.',
  SHIPMENT_PICKUP_END_BEFORE_START: 'La fecha de fin de recogida debe ser posterior a la de inicio.',
  SHIPMENT_IMAGE_FILE_REQUIRED: 'No se proporcionó ningún archivo.',
  SHIPMENT_NOT_FOUND_OR_UNAUTHORIZED: 'Envío no encontrado o sin autorización.',
  SHIPMENT_DRAFT_NOT_FOUND_OR_UNAUTHORIZED: 'Borrador no encontrado o sin autorización.',
  SHIPMENT_ID_OR_DRAFT_ID_REQUIRED: 'Se requiere el ID del envío o del borrador.',
  SHIPMENT_IMAGE_WRONG_SHIPMENT: 'La imagen no pertenece a este envío.',
  SHIPMENT_IMAGE_NOT_VERIFIED: 'La imagen todavía no fue verificada.',
  SHIPMENT_PICKUP_INVALID_STATUS: 'No se puede retirar el envío en su estado actual.',
  SHIPMENT_PACKAGE_REQUIRED: 'Se requiere al menos un paquete.',
  SHIPMENT_PACKAGE_FIELD_REQUIRED: 'Falta completar un campo obligatorio del paquete.',
  SHIPMENT_PACKAGE_FIELD_INVALID: 'Un campo del paquete debe ser mayor a 0.',
  SHIPMENT_PACKAGE_DIMENSION_TOO_LARGE: 'Una dimensión del paquete supera el máximo permitido (400 cm).',
  SHIPMENT_VEHICLE_REQUIRED: 'Se requiere al menos un vehículo.',
  SHIPMENT_VEHICLE_CATEGORY_REQUIRED: 'Falta indicar la categoría del vehículo.',
  SHIPMENT_MOVING_DETAILS_REQUIRED: 'Se requieren los detalles de la mudanza.',
  SHIPMENT_TIMESLOT_END_BEFORE_START: 'La hora de fin debe ser posterior a la de inicio.',
  SHIPMENT_CANCEL_USER_ID_REQUIRED: 'Se requiere el ID de usuario para cancelar el envío.',
  SHIPMENT_RECEIVER_NAME_EMPTY: 'El nombre de quien recibe no puede estar vacío.',
  SHIPMENT_RECEIVER_NAME_TOO_LONG: 'El nombre de quien recibe no puede superar los 255 caracteres.',
  SHIPMENT_RECEIVER_DOCUMENT_ID_EMPTY: 'El documento de identidad no puede estar vacío.',
  SHIPMENT_RECEIVER_DOCUMENT_ID_TOO_LONG: 'El documento de identidad no puede superar los 50 caracteres.',
  SHIPMENT_RECEIVER_SIGNATURE_EMPTY: 'La firma de conformidad no puede estar vacía.',
  SHIPMENT_VERIFICATION_CODE_LOCKED: 'El código de verificación está bloqueado. Solicitá uno nuevo.',
  SHIPMENT_VERIFICATION_CODE_INVALID: 'El código de verificación es incorrecto.',
  SHIPMENT_DELIVER_INVALID_STATUS: 'No se puede entregar el envío en su estado actual.',
  SHIPMENT_ECOMMERCE_CONTENT_LOCKED:
    'Los datos de la compra y el retiro los define el comercio. Solo podés modificar la entrega y las aclaraciones.',
  SHIPMENT_DELIVERY_WINDOW_INVALID: 'Revisá las fechas y el horario de entrega. Deben ser posteriores al retiro.',
  SHIPMENT_EDIT_NOT_OWNER: 'No tenés permiso para editar este envío.',
  SHIPMENT_EDIT_CLOSED: 'Este envío ya no se puede editar.',
  SHIPMENT_EDIT_STATUS_INVALID: 'El envío no se puede editar en su estado actual.',
  SHIPMENT_EDIT_EXPIRED: 'No se puede editar un envío vencido.',
  SHIPMENT_EDIT_NO_CHANGES: 'No se detectaron cambios.',
  SHIPMENT_EDIT_IMPACT_QUOTE_CONFIRMED:
    'No se pueden hacer cambios de impacto con una cotización ya confirmada. Cancelá y creá un nuevo envío.',
  SHIPMENT_EDIT_IMPACT_WITHIN_12H: 'No se pueden hacer cambios de impacto dentro de las 12 horas previas al retiro.',
  SHIPMENT_EDIT_PICKUP_START_WITHIN_12H: 'La nueva fecha de recogida debe ser al menos 12 horas después de ahora.',
  SHIPMENT_UNAUTHORIZED_VIEW: 'No tenés permiso para ver este envío.',
  SHIPMENT_REPUBLISH_PICKUP_START_REQUIRED: 'Se requiere la fecha de recogida para volver a publicar.',
  SHIPMENT_REPUBLISH_PICKUP_END_INVALID: 'La fecha de fin de recogida debe ser posterior a la de inicio.',
  SHIPMENT_REPUBLISH_TIME_SLOT_INVALID: 'La hora de fin debe ser posterior a la de inicio.',
  SHIPMENT_REPUBLISH_UNAUTHORIZED: 'No tenés permiso para volver a publicar este envío.',
  SHIPMENT_REPUBLISH_CANCELLED: 'No se puede volver a publicar: el envío fue cancelado.',
  SHIPMENT_REPUBLISH_INVALID_STATUS: 'El envío no se puede volver a publicar en su estado actual.',
  SHIPMENT_REPUBLISH_QUOTE_ACCEPTED: 'No se puede volver a publicar: ya se aceptó una cotización.',
  SHIPMENT_REPUBLISH_NOT_EXPIRED: 'El envío no está vencido. Usá la opción de editar para actualizarlo.',
  SHIPMENT_DRAFT_DATA_TOO_LARGE: 'Los datos del borrador superan el tamaño máximo permitido.',
  SHIPMENT_DRAFT_NOT_OWNER: 'Este borrador no te pertenece.',
  SHIPMENT_PICKUP_START_INVALID: 'La fecha de inicio de recogida no es válida.',
  SHIPMENT_PICKUP_END_INVALID: 'La fecha de fin de recogida no es válida.',
  SHIPMENT_ORIGIN_LAT_INVALID: 'La latitud de origen no es válida.',
  SHIPMENT_ORIGIN_LNG_INVALID: 'La longitud de origen no es válida.',
  SHIPMENT_DEST_LAT_INVALID: 'La latitud de destino no es válida.',
  SHIPMENT_DEST_LNG_INVALID: 'La longitud de destino no es válida.',

  // Auth, Client, Contact, Complaint
  CONTACT_FIELDS_MUST_BE_TEXT: 'El nombre, el correo y el mensaje deben ser texto.',
  CONTACT_EMAIL_FORMAT_INVALID: 'El formato del correo electrónico no es válido.',
  AUTH_EMAIL_ALREADY_REGISTERED: 'Ese correo electrónico ya está registrado.',
  CLIENT_EMAIL_FORMAT_INVALID: 'El formato del correo electrónico no es válido.',
  CLIENT_METHOD_INVALID: 'El método debe ser "email" o "google".',
  CLIENT_TERMS_DATE_INVALID: 'La fecha de aceptación de términos no es válida.',
  AUTH_UNAUTHORIZED: 'No autorizado.',
  AUTH_EMAIL_NOT_VERIFIED: 'Tu correo electrónico no está verificado.',
  AUTH_FORBIDDEN: 'No tenés permiso para realizar esta acción.',
  COMPLAINT_REPORTER_ID_REQUIRED: 'Falta el ID de quien reporta.',
  COMPLAINT_DESCRIPTION_REQUIRED: 'La descripción es obligatoria.',
  COMPLAINT_SHIPMENT_NOT_PAID: 'Solo se pueden reportar envíos que ya fueron pagados.',
  COMPLAINT_SHIPMENT_NO_DRIVER: 'El envío todavía no tiene un transportista asignado.',
  COMPLAINT_NOT_ASSOCIATED_WITH_SHIPMENT: 'No estás asociado a este envío.',
  COMPLAINT_REPORTED_USER_NOT_FOUND: 'No se encontró al usuario reportado.',
  COMPLAINT_ALREADY_FILED: 'Ya presentaste un reporte para este envío.',
  COMPLAINT_EVIDENCE_FILE_REQUIRED: 'No se proporcionó ningún archivo.',
  COMPLAINT_EVIDENCE_FILE_TYPE_INVALID: 'Tipo de archivo no permitido. Se aceptan JPG, PNG, HEIC, WEBP o PDF.',
  COMPLAINT_EVIDENCE_FILE_TOO_LARGE: 'El archivo es demasiado grande. Máximo 10 MB.',
  COMPLAINT_EVIDENCE_UNAUTHORIZED: 'No estás asociado a este reporte.',
  COMPLAINT_EVIDENCE_NOT_FOUND: 'No se encontró la evidencia.',
  COMPLAINT_NOT_OWNER: 'Este reporte no te pertenece.',

  // Driver: perfil, documentos, guest
  DRIVER_CI_INVALID: 'La cédula debe tener entre 1 y 8 dígitos.',
  DRIVER_RUT_INVALID: 'El RUT debe tener exactamente 12 dígitos.',
  DRIVER_LICENSE_ALREADY_REGISTERED: 'Este número de licencia ya está registrado a nombre de otro transportista.',
  DRIVER_CI_ALREADY_VERIFIED: 'Esta cédula ya está verificada para otro transportista.',
  DRIVER_RUT_ALREADY_REGISTERED: 'Este RUT ya está registrado para otro transportista.',
  DRIVER_EMAIL_ALREADY_ACTIVE: 'Ya existe un transportista activo con este correo electrónico.',
  DRIVER_OTHER_TYPE_REQUIRED: 'Si elegís "Otro", tenés que especificar el tipo.',
  DRIVER_OTHER_TYPE_TOO_LONG: 'El tipo de vehículo personalizado no puede superar los 100 caracteres.',
  DRIVER_VEHICLE_SUGGESTION_BRAND_MODEL_REQUIRED: 'Si elegís "Otro", tenés que indicar la marca y el modelo sugeridos.',
  DRIVER_VEHICLE_SUGGESTION_MODEL_REQUIRED: 'Si elegís "Otro", tenés que indicar el modelo sugerido.',
  DRIVER_VEHICLE_PLATE_ALREADY_VERIFIED: 'Ya existe un vehículo con esta patente registrado y verificado a nombre de otro transportista.',
  DRIVER_CHECK_FIELD_VALUE_REQUIRED: 'Faltan los parámetros necesarios para esta verificación.',
  DRIVER_CHECK_FIELD_INVALID: 'El campo indicado no es válido.',
  DRIVER_PROFILE_NOT_FOUND_FOR_USER: 'No se encontró tu perfil de transportista.',
  DRIVER_ALREADY_DISABLED: 'Tu cuenta ya está deshabilitada.',
  DRIVER_ACTIVE_SHIPMENTS_EXIST: 'No podés eliminar tu cuenta mientras tengas envíos activos.',
  DRIVER_EMAIL_FORMAT_INVALID: 'El formato del correo electrónico no es válido.',
  DRIVER_METHOD_INVALID: 'El método debe ser "email" o "google".',
  DRIVER_TERMS_TIMESTAMP_REQUIRED: 'Se requiere la fecha de aceptación de los términos.',
  DRIVER_TERMS_TIMESTAMP_INVALID: 'La fecha de aceptación de términos no es válida.',
  DRIVER_PRIVACY_TIMESTAMP_REQUIRED: 'Se requiere la fecha de aceptación de la política de privacidad.',
  DRIVER_NO_VALID_FIELDS: 'No se proporcionó ningún campo válido para actualizar.',
  DRIVER_LICENSE_NUMBER_REQUIRED: 'El número de licencia es obligatorio.',
  DRIVER_LICENSE_NUMBER_TYPE_INVALID: 'El número de licencia no es válido.',
  DRIVER_LICENSE_NUMBER_TOO_LONG: 'El número de licencia no puede superar los 50 caracteres.',
  DRIVER_LICENSE_NUMBER_FORMAT_INVALID: 'El formato del número de licencia no es válido.',
  DRIVER_BANK_CODE_INVALID: 'El banco indicado no es válido o no está soportado.',
  DRIVER_BANK_ACCOUNT_NUMBER_INVALID: 'El número de cuenta no tiene el formato correcto para el banco seleccionado.',
  DRIVER_ORIGIN_DEPARTMENTS_INVALID: 'Los departamentos de origen no son válidos.',
  DRIVER_DEPARTMENT_INVALID: 'Cada departamento debe ser un texto no vacío.',
  DRIVER_DEPARTMENT_TOO_LONG: 'El nombre del departamento no puede superar los 100 caracteres.',
  DRIVER_SHIPMENT_TYPES_INVALID: 'Los tipos de envío no son válidos.',
  DRIVER_SHIPMENT_TYPE_INVALID: 'El tipo de envío indicado no es válido.',
  DRIVER_CLOSING_FROM_INVALID: 'La fecha de inicio del cierre no es válida.',
  DRIVER_CLOSING_TO_INVALID: 'La fecha de fin del cierre no es válida.',
  DRIVER_CLOSING_RANGE_INVALID: 'La fecha de inicio del cierre no puede ser posterior a la de fin.',
  DRIVER_DOC_CONSENT_REQUIRED: 'Se requiere tu consentimiento para subir documentos de identidad.',
  DRIVER_DOC_FILE_TOO_SMALL: 'El archivo es demasiado pequeño.',
  DRIVER_DOC_FILE_TOO_LARGE: 'El archivo es demasiado grande. Máximo 10 MB.',
  DRIVER_DOC_PROFILE_NOT_FOUND_FOR_USER: 'No se encontró tu perfil de transportista.',
  DRIVER_DOC_VEHICLE_NOT_OWNED: 'Este vehículo no pertenece a tu perfil de transportista.',
  DRIVER_DOC_ALREADY_UNDER_REVIEW: 'Este documento ya está en revisión. No podés reemplazarlo hasta que sea aprobado o rechazado.',
  DRIVER_DOC_NOT_OWNER: 'Solo podés confirmar tus propios documentos.',
  DRIVER_DOC_STATUS_INVALID_TRANSITION: 'El documento no está en un estado válido para esta acción.',
  DRIVER_DOC_VALIDATION_FAILED: 'No se pudo validar el documento. Probá con otro archivo.',
  DRIVER_DOC_BIRTHDATE_REQUIRED: 'La fecha de nacimiento es obligatoria para enviar la verificación.',
  DRIVER_DOC_BIRTHDATE_FORMAT_INVALID: 'El formato de la fecha de nacimiento no es válido.',
  DRIVER_DOC_BIRTHDATE_FUTURE: 'La fecha de nacimiento no puede ser en el futuro.',
  DRIVER_DOC_MIN_AGE_NOT_MET: 'Tenés que ser mayor de 18 años para registrarte como transportista.',
  DRIVER_DOC_ID_FRONT_REQUIRED: 'Tenés que subir el frente de tu Documento de Identidad antes de continuar.',
  DRIVER_DOC_LICENSE_FRONT_REQUIRED: 'Tenés que subir el frente de tu Libreta de Conducir antes de continuar.',
  DRIVER_DOC_NO_ACTIVE_VEHICLE: 'No se encontró ningún vehículo activo. Registrá un vehículo primero.',
  DRIVER_DOC_VERIFICATION_ALREADY_IN_REVIEW: 'La verificación de tu vehículo ya está en revisión.',
  DRIVER_DOC_VEHICLE_ALREADY_VERIFIED: 'Tu vehículo ya está verificado.',
  DRIVER_DOC_LOGBOOK_FRONT_REQUIRED: 'Tenés que subir el frente de la tarjeta de propiedad antes de continuar.',
  DRIVER_DOC_INSURANCE_FRONT_REQUIRED: 'Tenés que subir el frente del SOAT antes de continuar.',
  GUEST_CODE_EXPIRED: 'El código expiró. Solicitá uno nuevo.',
  GUEST_CODE_LOCKED: 'Demasiados intentos fallidos. El código fue bloqueado.',
  GUEST_CODE_INVALID: 'El código ingresado es incorrecto.',

  // Invoicing, Legal, Location, Notification, Question, Review
  INVOICING_INVOICE_FORBIDDEN: 'No tenés permiso para acceder a esta factura.',
  INVOICING_PDF_NOT_ACCEPTED: 'El PDF todavía no está disponible: la factura debe estar aceptada.',
  INVOICING_PDF_MISSING_REFERENCE: 'El PDF todavía no está disponible para esta factura.',
  NOTIFICATION_VALIDATION_ERROR: 'Falta el token de notificaciones.',
  NOTIFICATION_PREFERENCES_DRIVER_ONLY: 'Las preferencias de notificación solo están disponibles para transportistas.',
  NOTIFICATION_UPDATE_PREFS_DRIVER_NOT_FOUND: 'No se encontró tu perfil de transportista.',
  NOTIFICATION_INVALID_DEPARTMENTS: 'Uno o más departamentos indicados no son válidos.',
  QUESTION_PHONE_NOT_ALLOWED:
    'No se pueden compartir números de teléfono en las preguntas. Continuá la conversación dentro de la plataforma.',
  QUESTION_SHIPMENT_NOT_ACCEPTING: 'Este envío ya no acepta preguntas.',
  QUESTION_OWN_SHIPMENT_FORBIDDEN: 'No podés hacer preguntas en tu propio envío.',
  QUESTION_DRIVER_PROFILE_NOT_FOUND: 'No se encontró tu perfil de transportista.',
  QUESTION_LIMIT_REACHED: 'Llegaste al límite de 5 preguntas para este envío.',
  REVIEW_COMMENT_INVALID_TYPE: 'El comentario no es válido.',
  REVIEW_UNAUTHORIZED: 'Solo quien creó el envío puede calificar al transportista.',

  // Saved address, Vehicle, Vehicle suggestion
  SAVED_ADDRESS_LABEL_EMPTY: 'El nombre de la dirección no puede estar vacío.',
  SAVED_ADDRESS_LABEL_FORMAT_INVALID: 'El nombre de la dirección solo puede tener letras, números, espacios, guiones y puntos.',
  SAVED_ADDRESS_COORDS_INVALID: 'Las coordenadas no son válidas.',
  SAVED_ADDRESS_OUTSIDE_REGION: 'Las coordenadas están fuera de la región soportada.',
  SAVED_ADDRESS_MAX_REACHED: 'Llegaste al máximo de 20 direcciones guardadas. Eliminá una para agregar otra.',
  VEHICLE_DRIVER_PROFILE_NOT_FOUND: 'No se encontró tu perfil de transportista.',
  VEHICLE_NOT_OWNER: 'Solo podés modificar tus propios vehículos.',
  VEHICLE_CHANGE_NOT_VERIFIED: 'Tu cuenta tiene que estar verificada antes de cambiar de vehículo.',
  VEHICLE_CHANGE_SHIPMENT_IN_TRANSIT: 'No podés cambiar de vehículo mientras tenés un envío en tránsito.',
  VEHICLE_CHANGE_ACTIVE_COMMITMENTS: 'Tenés compromisos activos con este vehículo.',
  VEHICLE_OTHER_TYPE_FIELD_REQUIRED: 'Si elegís "Otro", tenés que especificar el tipo de vehículo.',
  VEHICLE_PLATE_ALREADY_VERIFIED: 'Esta patente ya está registrada y verificada a nombre de otro transportista.',
  VEHICLE_SUGGESTION_DRIVER_PROFILE_REQUIRED: 'Solo un transportista puede enviar una sugerencia de vehículo.',
  VEHICLE_SUGGESTION_VALIDATION_ERROR: 'Falta el ID del transportista.',
  VEHICLE_SUGGESTION_DRIVER_MISMATCH: 'Solo podés enviar una sugerencia a nombre de tu propio perfil de transportista.',

  // Quote: los 5 códigos de elegibilidad del driver ya llegaban con `code` desde antes de esta
  // tanda — create/createMulti los reenvían vía su whitelist VERIFICATION_ERROR_CODES.
  DRIVER_PROFILE_INCOMPLETE: 'Completá tu perfil para comenzar a cotizar.',
  DRIVER_PHONE_NOT_VERIFIED: 'Verificá tu teléfono para comenzar a cotizar.',
  DRIVER_LICENSE_EXPIRED: 'Tu licencia de conducir está vencida. Actualizá la fecha de vencimiento en tu perfil para continuar cotizando.',
  DRIVER_DOCUMENTS_INCOMPLETE: 'Verificá tus documentos para comenzar a cotizar.',
  DRIVER_LICENSE_EXPIRES_BEFORE_PICKUP:
    'Tu licencia de conducir vence antes de la fecha de retiro del envío. Actualizá la fecha de vencimiento en tu perfil.',

  // NEX-1382 (2026-09, segunda tanda): quote.controller.ts y vehicle.controller.ts ya no
  // descartan `code` — ver nexvia-api-web fix/NEX-1382_standardize-errors (ambos controllers
  // migrados a next(err), cada throw re-verificado para que el status HTTP no cambie).
  // Releído el código real de los handlers ya migrados para este listado, no la auditoría
  // vieja. Varios de estos códigos antes solo se cubrían por ERROR_DICTIONARY (mensaje sin
  // interpolar) o no se podían cubrir en absoluto porque el mensaje llevaba un valor variable
  // (índice, estado, fecha de suspensión, tier) — eso ya no bloquea nada acá: CODE_DICTIONARY
  // reemplaza el mensaje entero por una traducción fija, sin depender de esa parte variable.

  // quote/ — update-quote.handler.ts
  QUOTE_UNAUTHORIZED_EDIT: 'No tenés permiso para editar esta cotización.',
  QUOTE_CANNOT_EDIT_STATUS: 'La cotización no se puede editar en su estado actual.',
  QUOTE_SHIPMENT_EXPIRED_CANNOT_EDIT: 'Este envío venció y ya no acepta ediciones de cotizaciones.',
  QUOTE_EXPIRED_CANNOT_EDIT: 'Esta cotización venció y ya no se puede editar.',
  QUOTE_VEHICLE_NOT_FOUND: 'No se encontró el vehículo para este transportista.',
  QUOTE_VEHICLE_NOT_ACTIVE: 'El vehículo no está activo.',
  QUOTE_DURATION_INVALID: 'La duración indicada no es válida.',

  // quote/ — quote-validation.helper.ts (compartido por create/createMulti/update)
  QUOTE_SHIPMENT_NOT_AVAILABLE_FOR_QUOTING: 'El envío no está disponible para cotizar.',
  QUOTE_TIMESLOT_INVALID: 'El horario del envío no es válido.',
  QUOTE_TIMESLOT_ENDED: 'El horario para este envío ya finalizó.',
  QUOTE_PROPOSED_DATE_OUT_OF_RANGE: 'La fecha propuesta tiene que estar dentro del rango disponible del envío.',
  QUOTE_TIMESLOT_FORMAT_INVALID: 'El formato del horario indicado no es válido.',
  QUOTE_SCHEDULE_CONFLICT_SAME_DAY: 'No podés cotizar: ya tenés una cotización aceptada en la misma fecha que genera un conflicto.',
  QUOTE_SCHEDULE_CONFLICT_TIMESLOT:
    'No podés cotizar: ya tenés una cotización aceptada en la misma fecha que genera un conflicto de horario.',
  QUOTE_MULTI_TIMESLOT_OVERLAP: 'Los horarios de las cotizaciones se superponen.',

  // quote/ — get-quote-by-id.handler.ts, list-quotes-by-shipment.handler.ts, list-my-quotes.handler.ts
  QUOTE_UNAUTHORIZED_VIEW: 'No tenés permiso para ver esta cotización.',
  QUOTE_UNAUTHORIZED_VIEW_SHIPMENT: 'No tenés permiso para ver las cotizaciones de este envío.',
  QUOTE_DRIVER_PROFILE_NOT_FOUND_FOR_USER: 'No se encontró el perfil de transportista para este usuario.',

  // quote/ — create-quote.handler.ts, create-multi-quote.handler.ts
  QUOTE_DRIVER_PROFILE_NOT_FOUND: 'No se encontró tu perfil de transportista.',
  QUOTE_DRIVER_ACCOUNT_DISABLED: 'Tu cuenta está deshabilitada. Reactivala para poder cotizar.',
  QUOTE_DRIVER_SUSPENDED_CREATE: 'Tu cuenta está suspendida. No podés crear cotizaciones.',
  QUOTE_DRIVER_NO_ACTIVE_VEHICLES: 'No tenés vehículos activos.',
  QUOTE_OWN_SHIPMENT_FORBIDDEN: 'No podés cotizar en tu propio envío.',
  QUOTE_MULTI_AT_LEAST_ONE_REQUIRED: 'Se requiere al menos una cotización.',
  QUOTE_MULTI_ITEM_INVALID: 'Una de las cotizaciones del lote no es válida.',
  QUOTE_MULTI_ITEM_AMOUNT_INVALID: 'El monto de una de las cotizaciones del lote no es válido.',
  QUOTE_MULTI_ITEM_DATE_REQUIRED: 'Falta la fecha propuesta en una de las cotizaciones del lote.',
  QUOTE_MULTI_ITEM_TIMESLOT_REQUIRED: 'Falta el horario propuesto en una de las cotizaciones del lote.',
  QUOTE_MULTI_BATCH_LIMIT_EXCEEDED: 'Se permiten un máximo de 3 cotizaciones por lote.',

  // quote/ — cancel-quote.handler.ts, get-cancellation-preview.handler.ts
  QUOTE_CANCEL_REASON_CODE_INVALID: 'El motivo indicado no es válido.',
  QUOTE_NOT_OWNER_CANCEL: 'Solo podés cancelar tus propias cotizaciones.',
  QUOTE_DRIVER_SUSPENDED_CANCEL: 'Tu cuenta está suspendida. No podés realizar esta acción.',
  QUOTE_CANCEL_SHIPMENT_IN_TRANSIT_OR_COMPLETED: 'No se puede cancelar una cotización de un envío que ya está en tránsito o completado.',
  QUOTE_NOT_CANCELLABLE: 'La cotización no está en un estado que permita cancelarla.',
  QUOTE_CANCELLATION_CONFIG_NOT_FOUND: 'No se pudo calcular la penalidad de cancelación. Contactá a soporte.',
  QUOTE_UNAUTHORIZED_VIEW_PREVIEW: 'Solo podés ver la vista previa de tus propias cotizaciones.',
  QUOTE_DRIVER_SUSPENDED_PREVIEW: 'Tu cuenta está suspendida. No podés realizar esta acción.',

  // vehicle/ — list-vehicles.handler.ts, get-vehicle-by-id.handler.ts (los otros códigos de
  // vehicle/ ya estaban arriba, agregados en la tanda anterior)
  VEHICLE_DRIVER_PROFILE_NOT_FOUND_SIMPLE: 'No se encontró tu perfil de transportista.',
  VEHICLE_UNAUTHORIZED_VIEW: 'No tenés permiso para ver este vehículo.',

  PASSENGER_TRANSPORT_DISABLED: 'El traslado de personas no está disponible por el momento.',
  PASSENGER_DETAIL_REQUIRED: 'Completá los datos del grupo.',
  PASSENGER_COUNT_INVALID: 'La cantidad de pasajeros tiene que ser entre 1 y 60.',
  PASSENGER_LUGGAGE_INVALID: 'Elegí qué equipaje llevan.',
  PASSENGER_EVENT_TYPE_INVALID: 'Elegí el tipo de evento.',
  PASSENGER_STOPS_INVALID: 'Revisá los puntos de encuentro: cada uno necesita una dirección elegida de la lista.',
  PASSENGER_RETURN_REQUIRED: 'Indicá la hora de regreso.',
  PASSENGER_RETURN_INVALID: 'El regreso tiene que ser después de la salida y dentro de las 24 horas siguientes.',
  PASSENGER_BOOKING_TOO_FAR: 'La fecha de salida está demasiado lejos. Elegí una fecha más cercana.',
  PASSENGER_DEPARTURE_TOO_SOON: 'La salida es demasiado pronto para recibir cotizaciones. Elegí una fecha u hora más adelante.',
  PASSENGER_REPUBLISH_NOT_SUPPORTED: 'Un traslado vencido no se puede volver a publicar. Creá una nueva solicitud.',
  PASSENGER_QUOTE_NO_LONGER_AVAILABLE: 'Este transportista ya no puede hacer el traslado. Elegí otra cotización.',
  SHIPMENT_LOCAL_TIME_INVALID: 'No pudimos interpretar la fecha y hora indicadas. Revisalas e intentá de nuevo.'
};

/**
 * NEX-1151 (audit W1) — the backend-wide generic codes (nexvia-api-web shared/errors/app-error.ts,
 * validation.error.ts and validate.ts's Zod border). Each covers many different messages, so the text
 * is generic on purpose (this dictionary is global). Unlike CODE_DICTIONARY it is consulted AFTER the
 * message-keyed ERROR_DICTIONARY: a message with its own translation ('Quote not found' arrives with
 * RESOURCE_NOT_FOUND) keeps it; only an untranslated message falls back to these.
 */
export const GENERIC_CODE_DICTIONARY: MessageDictionary = {
  OPERATION_FAILED: 'No pudimos completar la operación. Intentá nuevamente en unos minutos.',
  RESOURCE_NOT_FOUND: 'No encontramos lo que buscás. Puede que ya no exista.',
  VALIDATION_ID_REQUIRED: 'Falta indicar sobre qué querés hacer esta acción. Actualizá la página e intentá nuevamente.',
  VALIDATION_INVALID_ID: 'El enlace o el identificador no es válido. Actualizá la página e intentá nuevamente.',
  VALIDATION_FIELD_REQUIRED: 'Falta completar un dato obligatorio. Revisá el formulario.',
  VALIDATION_FIELD_INVALID: 'Alguno de los datos no es válido. Revisá el formulario.',
  VALIDATION_ENUM_INVALID: 'Alguna de las opciones elegidas ya no está disponible. Revisá el formulario.',
  VALIDATION_FIELD_TOO_LONG: 'Alguno de los textos es demasiado largo. Acortalo e intentá nuevamente.'
};

/**
 * Pending audit 2026-10-30 (WEB-1/WEB-2) — what `utils/axios.ts` puts on an error whose answer had
 * no usable message (an HTML error page from the proxy, a Blob that was not JSON, an envelope
 * without text). Deliberately the same words as `OPERATION_FAILED`, and deliberately NOT a dictionary
 * entry keyed by the old 'Wrong Services': `translateKnownError` must keep answering `null` for
 * these so each screen's own, more specific fallback still wins.
 */
export const GENERIC_ERROR_MESSAGE = GENERIC_CODE_DICTIONARY.OPERATION_FAILED;

// NEX-1151 (audit W1) — the message lookup ignores case: the API answers "Internal server error"
// (app-error.ts, error.middleware.ts) while this dictionary has always said 'Internal Server Error'.
const ERROR_DICTIONARY_BY_LOWERCASE = new Map(Object.entries(ERROR_DICTIONARY).map(([key, value]) => [key.toLowerCase(), value]));

function lookupKnownTranslation(message: unknown, code?: string | null): string | null {
  if (code && CODE_DICTIONARY[code]) return CODE_DICTIONARY[code];
  if (typeof message === 'string' && message) {
    const byMessage = ERROR_DICTIONARY[message] ?? ERROR_DICTIONARY_BY_LOWERCASE.get(message.toLowerCase());
    if (byMessage) return byMessage;
  }
  if (code && GENERIC_CODE_DICTIONARY[code]) return GENERIC_CODE_DICTIONARY[code];
  return null;
}

/**
 * NEX-1151 (audit W1) — the Spanish text a dictionary has for this error, or `null`: never the raw
 * backend message (English), for callers that show their own fallback instead.
 */
export const translateKnownError = (message: unknown, code?: string | null): string | null => lookupKnownTranslation(message, code);

// `message` deliberately stays generic instead of `string | null | undefined`: callers pass
// whatever the backend/axios happened to hand them (a string, but also `null`/`undefined`/a
// number/a plain object have all been observed and are exercised by
// src/__tests__/utils/error-translator.test.js) and get the exact same value back unchanged
// whenever it isn't a translatable string — this function was never a strict
// `string -> string` mapper, and forcing that shape now would be a behavior change, not a type.
export const translateError = <T>(message: T, code?: string | null): T | string => {
  const known = lookupKnownTranslation(message, code);
  if (known) return known;

  if (!message || typeof message !== 'string') return message;

  if (message.includes('must be greater than 0')) {
    return message.replace('must be greater than 0', 'debe ser mayor a 0');
  }

  return message;
};
